import React, { useState, useMemo } from 'react';

const PAGE_SIZE = 20;

const fmtDate = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
};

const COLUMNS = [
  { key: 'name',        label: '名前',     align: 'left'  },
  { key: 'applied',     label: '応募回数', align: 'right' },
  { key: 'won',         label: '当選回数', align: 'right' },
  { key: 'last_won_at', label: '前回当選', align: 'right' },
  { key: 'hasAccount',  label: 'アカウント', align: 'center' },
];

/**
 * 応募者履歴の一覧（検索・ソート・ページング）。
 * @param {object} stats - events/{eventId}/applicant_stats の中身
 */
const ApplicantHistoryModal = ({ open, onClose, stats = {}, eventName }) => {
  const [query, setQuery]     = useState('');
  const [sortKey, setSortKey] = useState('won');
  const [sortDir, setSortDir] = useState('desc'); // 'asc' | 'desc'
  const [page, setPage]       = useState(1);

  const rows = useMemo(() => {
    return Object.entries(stats).map(([key, v]) => ({
      key,
      name: v.name || (key.startsWith('name:') ? key.slice(5) : '(名前なし)'),
      applied: v.applied || 0,
      won: v.won || 0,
      last_won_at: v.last_won_at || 0,
      hasAccount: !key.startsWith('name:'),
    }));
  }, [stats]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q ? rows.filter((r) => r.name.toLowerCase().includes(q)) : rows;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...base].sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (sortKey === 'name') return a.name.localeCompare(b.name, 'ja') * dir;
      if (sortKey === 'hasAccount') { av = av ? 1 : 0; bv = bv ? 1 : 0; }
      return (av - bv) * dir;
    });
  }, [rows, query, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const curPage    = Math.min(page, totalPages);
  const pageRows   = filtered.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE);

  const onSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
    setPage(1);
  };

  if (!open) return null;

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        {/* ヘッダー */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h2 style={S.title}>📊 応募者履歴</h2>
            <p style={S.sub}>{eventName ? `${eventName} の過去の応募・当選状況` : 'このイベントの過去の応募・当選状況'}（{filtered.length}人）</p>
          </div>
          <button onClick={onClose} style={S.closeBtn}>×</button>
        </div>

        {/* 検索 */}
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          placeholder="名前で検索"
          style={S.search}
        />

        {rows.length === 0 ? (
          <div style={S.empty}>まだ履歴がありません。<br />抽選を確定して準備中に戻すと、その回の応募・当選が記録されます。</div>
        ) : (
          <>
            {/* テーブル */}
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr>
                    {COLUMNS.map((c) => (
                      <th
                        key={c.key}
                        onClick={() => onSort(c.key)}
                        style={{ ...S.th, textAlign: c.align, cursor: 'pointer' }}
                      >
                        {c.label}
                        {sortKey === c.key && <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r) => (
                    <tr key={r.key} style={S.tr}>
                      <td style={{ ...S.td, textAlign: 'left', fontWeight: 700, color: '#1e293b' }}>{r.name}</td>
                      <td style={{ ...S.td, textAlign: 'right' }}>{r.applied}</td>
                      <td style={{ ...S.td, textAlign: 'right', fontWeight: 700, color: r.won > 0 ? '#6c63ff' : '#94a3b8' }}>{r.won}</td>
                      <td style={{ ...S.td, textAlign: 'right', color: '#64748b' }}>{fmtDate(r.last_won_at)}</td>
                      <td style={{ ...S.td, textAlign: 'center' }}>
                        {r.hasAccount
                          ? <span style={S.accountYes}>✓ あり</span>
                          : <span style={S.accountNo}>名前のみ</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ページング */}
            {totalPages > 1 && (
              <div style={S.pager}>
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={curPage <= 1} style={S.pagerBtn}>← 前へ</button>
                <span style={S.pagerInfo}>{curPage} / {totalPages}</span>
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={curPage >= totalPages} style={S.pagerBtn}>次へ →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const S = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20,
  },
  modal: {
    background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 680,
    maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 40px rgba(0,0,0,0.18)',
  },
  title: { fontSize: 19, fontWeight: 800, color: '#0f172a', margin: 0 },
  sub: { fontSize: 13, color: '#94a3b8', margin: '4px 0 0' },
  closeBtn: { background: 'none', border: 'none', fontSize: 22, color: '#94a3b8', cursor: 'pointer', lineHeight: 1 },
  search: {
    width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0',
    fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 16,
  },
  tableWrap: { border: '1px solid #eef2f7', borderRadius: 12, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    padding: '10px 12px', background: '#f8fafc', fontSize: 12, fontWeight: 800, color: '#475569',
    borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap', userSelect: 'none',
  },
  tr: { borderBottom: '1px solid #f1f5f9' },
  td: { padding: '10px 12px', fontSize: 13, color: '#334155' },
  accountYes: { fontSize: 11, fontWeight: 700, color: '#059669', background: '#ecfdf5', borderRadius: 6, padding: '2px 8px' },
  accountNo:  { fontSize: 11, fontWeight: 700, color: '#94a3b8', background: '#f1f5f9', borderRadius: 6, padding: '2px 8px' },
  empty: { textAlign: 'center', padding: '40px 16px', color: '#94a3b8', fontSize: 13, lineHeight: 1.8 },
  pager: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginTop: 16 },
  pagerBtn: {
    padding: '8px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff',
    fontSize: 13, fontWeight: 700, color: '#475569', cursor: 'pointer',
  },
  pagerInfo: { fontSize: 13, fontWeight: 700, color: '#64748b' },
};

export default ApplicantHistoryModal;
